// 消息处理日志相关功能
import { atom } from 'jotai';
import { fetchApi } from '../../../lib/apiClient';
import { 
  ProcessLogState, 
  ProcessLogStep, 
  LlmTextContent, 
  ToolRequestContent 
} from './types';

// 使用 initiatingUserMessageId 作为键存储处理日志
export const messageProcessLogAtom = atom<Record<string, ProcessLogState>>({});

/**
 * 获取消息处理日志
 * 使用 initiatingUserMessageId (触发用户消息ID) 作为查询参数
 */
export const fetchProcessLogAtom = atom(
  null,
  async (get, set, initiatingUserMessageId: string) => {
    // 检查是否已经在加载或已加载
    const currentState = get(messageProcessLogAtom)[initiatingUserMessageId];
    if (currentState?.isLoading) return;

    try {
      // 设置加载状态
      set(messageProcessLogAtom, prev => ({
        ...prev,
        [initiatingUserMessageId]: {
          ...(prev[initiatingUserMessageId] || {}), // 防御性处理，确保 key 存在
          isLoading: true,
          error: undefined
        }
      }));

      // 使用新的 API 路径，基于 initiatingUserMessageId 查询
      const path = `/chat/messages/${initiatingUserMessageId}/process-logs`;
      console.log(`调用 API 获取日志: ${path}`);

      const data = await fetchApi<{ logs: ProcessLogStep[] }>(path, {
        method: 'GET'
      });

      // 更新状态
      set(messageProcessLogAtom, prev => ({
        ...prev,
        [initiatingUserMessageId]: {
          steps: data.logs || [],
          isLoading: false,
          error: undefined
        }
      }));
    } catch (error) {
      console.error('获取消息处理日志失败:', error);

      // 更新错误状态
      set(messageProcessLogAtom, prev => ({
        ...prev,
        [initiatingUserMessageId]: {
          ...(prev[initiatingUserMessageId] || {}), // 防御性处理
          isLoading: false,
          error: error instanceof Error ? error.message : '未知错误'
        }
      }));
    }
  }
);

// Helper function to update message process log
export const logStep = (set: any, initiatingId: string, stepData: Omit<ProcessLogStep, 'id' | 'timestamp' | 'sequence'>) => {
  set(messageProcessLogAtom, (prev: Record<string, ProcessLogState>) => {
    const currentLog = prev[initiatingId] || { steps: [], isLoading: false };
    // Build the new step safely, using field names from ProcessLogStep type
    const newStep: ProcessLogStep = {
      id: `step-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      sequence: (currentLog.steps?.length || 0) + 1,
      timestamp: new Date().toISOString(),
      processType: stepData.processType,
      processData: stepData.processData,
    };
    return {
      ...prev,
      [initiatingId]: {
        ...currentLog,
        steps: [...(currentLog.steps || []), newStep],
      },
    };
  });
};

// Helper function to update or add a single LLM text step in the log
export const updateOrAddLlmStep = (set: any, initiatingId: string, textDelta: string) => {
  set(messageProcessLogAtom, (prev: Record<string, ProcessLogState>) => {
    const currentLog = prev[initiatingId] || { steps: [], isLoading: false };
    const steps = currentLog.steps || [];

    // Find the index of the *last* existing llm_text step
    let lastLlmStepIndex = -1;
    for (let i = steps.length - 1; i >= 0; i--) {
      if (steps[i].processType === 'llm_text') {
        lastLlmStepIndex = i;
        break;
      }
    }

    let updatedSteps;
    if (lastLlmStepIndex !== -1) {
      // Update existing llm_text step
      updatedSteps = [...steps];
      const existingStep = updatedSteps[lastLlmStepIndex] as ProcessLogStep & { processData: LlmTextContent }; // Assert type
      updatedSteps[lastLlmStepIndex] = {
        ...existingStep,
        processData: {
          text: (existingStep.processData.text ?? '') + textDelta,
        },
        timestamp: new Date().toISOString(), // Update timestamp on modification
      };
    } else {
      // Add new llm_text step
      const newStep: ProcessLogStep = {
        id: `step-llm-${Date.now()}`,
        sequence: steps.length + 1,
        processType: 'llm_text',
        processData: { text: textDelta },
        timestamp: new Date().toISOString(),
      };
      updatedSteps = [...steps, newStep];
    }

    return {
      ...prev,
      [initiatingId]: {
        ...currentLog,
        steps: updatedSteps,
      },
    };
  });
};

// Helper function to update the status of a tool_request step in the log
export const updateToolRequestStepStatus = (
  set: any, 
  initiatingId: string, 
  toolId: string, 
  newStatus: 'complete' | 'error' | 'stopped',
  onlyIfRunning: boolean = false // Optionally only update if current status is 'running'
) => {
  set(messageProcessLogAtom, (prev: Record<string, ProcessLogState>) => {
    const currentLog = prev[initiatingId];
    if (!currentLog || !currentLog.steps) return prev;

    let changed = false;
    const updatedSteps = currentLog.steps.map((step: ProcessLogStep) => {
      if (step.processType === 'tool_request') {
        const toolContent = step.processData as ToolRequestContent;
        if (toolContent.toolId === toolId &&
            (!onlyIfRunning || toolContent.status === 'running'))
        {
          if (toolContent.status !== newStatus) {
            changed = true;
            return {
              ...step,
              processData: {
                ...toolContent,
                status: newStatus,
              },
            };
          }
        }
      }
      return step;
    });

    // Only return new state if something actually changed
    if (!changed) {
      return prev;
    }

    return {
      ...prev,
      [initiatingId]: {
        ...currentLog,
        steps: updatedSteps,
      },
    };
  });
};
